import undoRedoManager from './UndoRedoManager';
import { captureGridHistoryState } from './gridHistory';
import { flushPendingGridHistoryCommit } from './gridHistoryFlush';
import { cancelPendingGridHistoryCommit } from './gridHistoryCommit';
import { getLastCommittedGridHistory } from './gridHistoryBaseline';
import {
  isGridHistoryReady,
  suppressGridHistory,
} from './gridHistorySuppress';

/**
 * Commit any in-flight debounced edit, then capture a fresh "before" snapshot
 * for a discrete user action (delete, duplicate, add shape, paste, …).
 */
export function captureDiscreteHistoryBefore(scene) {
  if (!scene) return null;

  flushPendingGridHistoryCommit(
    (before, targetScene, label) =>
      undoRedoManager.recordGridHistoryTransitionCore(before, targetScene, label),
    'edit'
  );
  cancelPendingGridHistoryCommit();

  return captureGridHistoryState(scene);
}

/** Record one undo step using an explicit pre-mutation snapshot (no extra flush). */
export function recordDiscreteHistoryAfter(beforeSnapshot, scene, label = 'edit') {
  if (!beforeSnapshot || !scene) return;
  undoRedoManager.recordGridHistoryTransitionCore(beforeSnapshot, scene, label);
}

/** Flush pending edits, capture before, run mutation under suppress, record one step. */
export function runDiscreteGridHistoryStep(scene, label, mutateFn) {
  if (!scene || typeof mutateFn !== 'function') return;

  const before = captureDiscreteHistoryBefore(scene);
  if (!before) {
    mutateFn();
    return;
  }

  mutateFn();
  recordDiscreteHistoryAfter(before, scene, label);
}

export function resolveHistoryBeforeSnapshot(explicitBefore) {
  return explicitBefore || getLastCommittedGridHistory();
}

/**
 * One undo step for async mesh/GLB adds (upload, library import).
 * Call before loader.load; finish() in the load callback after scene + Redux updates.
 */
export function beginSuppressedMeshAddHistory(scene) {
  const before =
    scene && isGridHistoryReady() ? captureDiscreteHistoryBefore(scene) : null;
  let releaseSuppress = () => {};

  if (before) {
    releaseSuppress = suppressGridHistory();
  }

  return {
    before,
    finish(label = 'add mesh') {
      releaseSuppress();
      if (before && scene) {
        recordDiscreteHistoryAfter(before, scene, label);
      }
    },
    cancel() {
      releaseSuppress();
    },
  };
}
